import { memo, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowUpRight,
  CalendarCheck,
  CheckCircle2,
  Circle,
  Eye,
  FileText,
  Mail,
  Plus,
  Upload,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  buttonColorClasses,
  iconForTitle,
} from "@/lib/checklistButtons";
import { ACTION_ICON, ctaFor, formatBytes } from "./constants";

// Backend enforces the same cap — keep them in sync.
const MAX_ATTACHMENTS_PER_ITEM = 10;

function ChecklistCardBase({
  item,
  onToggle,
  onMarkViewed,
  viewer,
  onOpenFinalExam,
  examLocked,
  onUpload,
  onRemoveAttachment,
}) {
  const ActionIcon = ACTION_ICON[item.action_type] || Eye;
  const cta = ctaFor(item);
  const CtaIcon = cta.icon;
  const isFinalExam = item.action_type === "exam";
  const isSchedule = item.action_type === "schedule";
  // When the admin has explicitly configured a `buttons` array, IT becomes the
  // sole source of truth for CTAs on this card — the legacy
  // primary/secondary/auto-upload buttons stay hidden. This is what lets a
  // "Photo ID" item render with a single "Upload" button instead of the
  // stacked triple-button set the client flagged.
  const hasCustomButtons = Array.isArray(item.buttons) && item.buttons.length > 0;
  const customButtons = hasCustomButtons ? item.buttons : [];
  // canAttach = the file input + upload logic below should be wired up. True
  // for legacy sign/upload items OR any item whose custom-button array
  // contains an action=upload button (see backend's matching check).
  const canAttach =
    item.action_type === "sign" ||
    item.action_type === "upload" ||
    (hasCustomButtons && customButtons.some((b) => b.action === "upload"));
  // Final Exam is only "openable" if every non-exam item is done.
  const hasLink = isFinalExam ? !examLocked : !!item.external_url;
  const fileInputRef = useRef(null);
  // Calendly inline widget mount point — populated when `initInlineWidget`
  // runs. Kept as a ref so the effect can imperatively poke Calendly's script
  // at the exact DOM node without triggering re-renders.
  const calendlyRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  // Track which specific file is currently being removed so REPLACE/REMOVE on
  // sibling rows aren't grayed out during a single-file operation.
  const [removingFileId, setRemovingFileId] = useState(null);

  const attachments = Array.isArray(item.attachments) ? item.attachments : [];
  const hasAttachments = attachments.length > 0;
  const atCap = attachments.length >= MAX_ATTACHMENTS_PER_ITEM;

  // Mount the Calendly inline widget into `calendlyRef` for schedule items.
  // We render the calendar RIGHT INSIDE THE CARD so the photographer books
  // without leaving the portal — no popup, no new tab. Booking triggers a
  // postMessage that the dashboard already listens for and auto-ticks the
  // item. Once completed, we tear the widget down (no need to keep it
  // mounted after they've booked).
  useEffect(() => {
    if (!isSchedule) return;
    if (item.completed) return;
    if (!item.external_url) return;
    const target = calendlyRef.current;
    if (!target) return;

    let cancelled = false;
    // widget.js is loaded async in index.html — poll briefly until it's ready.
    const mount = () => {
      if (cancelled) return;
      const Calendly = window.Calendly;
      if (Calendly && typeof Calendly.initInlineWidget === "function") {
        target.innerHTML = ""; // clear any prior mount before re-init
        Calendly.initInlineWidget({
          url: item.external_url,
          parentElement: target,
        });
        return;
      }
      // Retry every 150ms up to ~6 seconds — well beyond a typical script load.
      const t = setTimeout(mount, 150);
      // Store the timer so cleanup can cancel it.
      target.__calendlyRetry = t;
    };
    mount();
    return () => {
      cancelled = true;
      if (target.__calendlyRetry) clearTimeout(target.__calendlyRetry);
      target.innerHTML = "";
    };
  }, [isSchedule, item.completed, item.external_url]);

  const openLink = async () => {
    if (isFinalExam) {
      if (examLocked) return;
      onOpenFinalExam();
      return;
    }
    // Schedule action: open the Calendly popup INSIDE the portal instead of
    // sending the photographer to a new tab. The widget.js script is loaded
    // globally from index.html; if it hasn't finished loading yet (unlikely
    // but possible on the very first paint) fall back to a new-tab window so
    // the CTA never becomes a dead click.
    if (item.action_type === "schedule" && item.external_url) {
      const Calendly = typeof window !== "undefined" ? window.Calendly : null;
      if (Calendly && typeof Calendly.initPopupWidget === "function") {
        Calendly.initPopupWidget({ url: item.external_url });
      } else {
        window.open(item.external_url, "_blank", "noopener,noreferrer");
      }
      try { await onMarkViewed(item.id); } catch (err) { console.error(err); }
      return;
    }
    if (item.external_url) {
      window.open(item.external_url, "_blank", "noopener,noreferrer");
    }
    try {
      await onMarkViewed(item.id);
    } catch (err) {
      console.error("mark-viewed failed", err);
    }
  };

  const emailSignedCopy = () => {
    const from = viewer?.name || viewer?.email || "";
    const subject = encodeURIComponent(`Signed copy · ${item.title}${from ? " · " + from : ""}`);
    const body = encodeURIComponent(
      `Hi Reelty team,\n\nAttached is my signed copy of "${item.title}".\n\nThanks!\n${viewer?.name || ""}`
    );
    window.location.href = `mailto:support@goreelty.com?subject=${subject}&body=${body}`;
  };

  const pickFile = () => fileInputRef.current && fileInputRef.current.click();

  const onFilePicked = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      toast.error("File exceeds the 25 MB limit.");
      return;
    }
    const allowed = ["application/pdf", "image/jpeg", "image/jpg", "image/png"];
    if (!allowed.includes(file.type)) {
      toast.error("Only PDF, JPG, or PNG files are accepted.");
      return;
    }
    if (atCap) {
      toast.error(`This item is at the ${MAX_ATTACHMENTS_PER_ITEM}-file limit. Remove one first.`);
      return;
    }
    setUploading(true);
    try {
      await onUpload(item.id, file);
    } finally {
      setUploading(false);
    }
  };

  const removeOne = async (fileId) => {
    setRemovingFileId(fileId);
    try {
      await onRemoveAttachment(item.id, fileId);
    } finally {
      setRemovingFileId(null);
    }
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      data-testid={`checklist-item-${item.id}`}
      id={`item-${item.id}`}
      className={`card-surface rounded-sm p-5 flex gap-5 items-start transition-all ${
        item.completed ? "border-[#6B0F1A]/25" : "hover:border-[#6B0F1A]/40 hover:translate-x-0.5"
      }`}
    >
      <Checkbox
        data-testid={`checklist-toggle-${item.id}`}
        checked={item.completed}
        disabled={isFinalExam}
        onCheckedChange={(v) => !isFinalExam && onToggle(item.id, Boolean(v))}
        title={isFinalExam ? "Complete this by passing the Final Exam" : undefined}
        className={`mt-1 w-5 h-5 border-[#1A0F0A]/25 data-[state=checked]:bg-[#6B0F1A] data-[state=checked]:border-[#6B0F1A] data-[state=checked]:text-[#F5EBD9] rounded-sm ${
          isFinalExam ? "opacity-60 cursor-not-allowed" : ""
        }`}
      />

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1.5">
          <span className="font-mono-tag text-[9px] text-[#78584B] flex items-center gap-1.5">
            <ActionIcon className="w-3 h-3" strokeWidth={1.75} />
            {item.action_type === "exam"
              ? "Exam"
              : item.action_type === "sign"
                ? "Signature required"
                : item.action_type === "setup"
                  ? "Setup"
                  : item.action_type === "upload"
                    ? "Upload required"
                    : item.action_type === "schedule"
                      ? "Book a call"
                      : "Review"}
          </span>
          {item.viewed_at && !item.completed && (
            <span className="font-mono-tag text-[9px] text-[#B08D57]">Viewed</span>
          )}
          {hasAttachments && (
            <span
              className="font-mono-tag text-[9px] text-[#6B0F1A]"
              data-testid={`checklist-attachment-count-${item.id}`}
            >
              {attachments.length} {attachments.length === 1 ? "FILE" : "FILES"}
            </span>
          )}
        </div>
        <h3 className={`font-display text-lg font-semibold tracking-tight ${item.completed ? "text-[#78584B] line-through" : "text-[#1A0F0A]"}`}>
          {item.title}
        </h3>
        {item.description && <p className="text-sm text-[#4D3F35] mt-1.5 leading-relaxed">{item.description}</p>}

        <div className="mt-4 flex items-center gap-3 flex-wrap">
          {/* ADMIN-CUSTOMIZED buttons — rendered in the order the admin
              configured. Each button carries its own color (primary/secondary/
              gold), action (url|upload), and inferred icon. When ANY custom
              buttons exist, the legacy primary/secondary/auto-upload block
              below is fully skipped so admins get exactly what they
              configured — nothing more. */}
          {hasCustomButtons && customButtons.map((btn, i) => {
            const BtnIcon = iconForTitle(btn.title);
            const isUploadBtn = btn.action === "upload";
            const uploadDisabled = isUploadBtn && (uploading || atCap);
            const urlDisabled = !isUploadBtn && !btn.url;
            const disabled = uploadDisabled || urlDisabled;
            const onClick = () => {
              if (isUploadBtn) {
                if (!uploadDisabled) pickFile();
                return;
              }
              if (!btn.url) return;
              window.open(btn.url, "_blank", "noopener,noreferrer");
              onMarkViewed(item.id).catch(() => {});
            };
            return (
              <Button
                key={`${btn.title}-${i}`}
                onClick={onClick}
                disabled={disabled}
                data-testid={`checklist-custom-btn-${item.id}-${i}`}
                className={`h-9 rounded-sm text-xs font-bold tracking-wide inline-flex items-center gap-2 ${
                  disabled
                    ? "bg-[#1A0F0A]/8 text-[#78584B] cursor-not-allowed hover:bg-[#1A0F0A]/8 border border-transparent"
                    : buttonColorClasses(btn.color)
                }`}
              >
                <BtnIcon className="w-3.5 h-3.5" strokeWidth={2} />
                {isUploadBtn && uploading ? "Uploading…" : (isUploadBtn && hasAttachments ? `${btn.title} another` : btn.title)}
                {!isUploadBtn && btn.url && <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={2} />}
              </Button>
            );
          })}

          {/* LEGACY primary CTA — only when the admin hasn't configured custom
              buttons. The document DOWNLOAD for sign/upload items (so the
              photographer can grab, print, and sign the doc), or the standard
              action link otherwise. Hidden for schedule items where the
              inline Calendly widget IS the action. */}
          {!hasCustomButtons && !isSchedule && (
            <Button
              onClick={openLink}
              disabled={!hasLink}
              data-testid={`checklist-cta-${item.id}`}
              className={`h-9 rounded-sm text-xs font-bold tracking-wide inline-flex items-center gap-2 ${
                hasLink
                  ? "bg-[#6B0F1A] hover:bg-[#8A1424] text-[#F5EBD9] btn-primary-glow"
                  : "bg-[#1A0F0A]/8 text-[#78584B] cursor-not-allowed hover:bg-[#1A0F0A]/8"
              }`}
            >
              <CtaIcon className="w-3.5 h-3.5" strokeWidth={2} />
              {/* Admin-editable primary label overrides the action-type default. */}
              {item.primary_label || cta.label}
              {hasLink && <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={2} />}
            </Button>
          )}
          {/* LEGACY secondary ghost button — same gating as above. */}
          {!hasCustomButtons && !isSchedule && item.secondary_label && item.secondary_url && (
            <Button
              onClick={() => {
                window.open(item.secondary_url, "_blank", "noopener,noreferrer");
                onMarkViewed(item.id).catch(() => {});
              }}
              variant="ghost"
              data-testid={`checklist-secondary-cta-${item.id}`}
              className="h-9 rounded-sm text-xs font-bold tracking-wide inline-flex items-center gap-2 bg-transparent text-[#6B0F1A] hover:bg-[#6B0F1A]/8 border border-[#6B0F1A]/40"
            >
              {item.secondary_label}
              <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={2} />
            </Button>
          )}
          {/* LEGACY auto-Upload button — only for sign/upload items when the
              admin HASN'T configured custom buttons. Once they do, the upload
              action lives inside their configured `buttons` array (or not at
              all — their choice). */}
          {!hasCustomButtons && canAttach && !atCap && (
            <Button
              onClick={pickFile}
              disabled={uploading}
              data-testid={hasAttachments
                ? `checklist-add-another-btn-${item.id}`
                : `checklist-upload-btn-${item.id}`}
              variant="ghost"
              className="h-9 rounded-sm text-xs font-bold tracking-wide inline-flex items-center gap-2 bg-transparent text-[#6B0F1A] hover:bg-[#6B0F1A]/8 border border-[#6B0F1A]/40 disabled:opacity-60"
            >
              {hasAttachments ? <Plus className="w-3.5 h-3.5" strokeWidth={2} /> : <Upload className="w-3.5 h-3.5" strokeWidth={2} />}
              {uploading ? "Uploading…" : hasAttachments ? "Add another" : "Upload"}
            </Button>
          )}
          {canAttach && atCap && (
            <span
              className="font-mono-tag text-[9px] text-[#78584B]"
              data-testid={`checklist-upload-cap-${item.id}`}
              title={`Up to ${MAX_ATTACHMENTS_PER_ITEM} files per item — remove one to add another.`}
            >
              {MAX_ATTACHMENTS_PER_ITEM}-FILE LIMIT REACHED
            </span>
          )}
          {canAttach && (
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf,image/jpeg,image/jpg,image/png"
              onChange={onFilePicked}
              className="hidden"
              data-testid={`checklist-upload-input-${item.id}`}
            />
          )}
          {!hasCustomButtons && !hasLink && !isSchedule && (
            <span
              className="font-mono-tag text-[9px] text-[#78584B]"
              data-testid={`checklist-cta-awaiting-${item.id}`}
            >
              {isFinalExam ? "COMPLETE OTHER STEPS FIRST" : "LINK COMING SOON"}
            </span>
          )}
        </div>

        {/* Inline Calendly widget — renders directly inside the schedule card
            once the phase is unlocked, so booking happens without leaving the
            portal. Torn down once the item is complete. */}
        {isSchedule && !item.completed && item.external_url && (
          <div
            className="mt-5 rounded-sm overflow-hidden border border-[#1A0F0A]/10 bg-white"
            data-testid={`checklist-calendly-inline-${item.id}`}
          >
            <div
              ref={calendlyRef}
              className="calendly-inline-widget"
              style={{ minWidth: "320px", height: "700px" }}
            />
          </div>
        )}
        {isSchedule && item.completed && (
          <div
            className="mt-5 rounded-sm border border-[#6B0F1A]/30 bg-[#6B0F1A]/8 p-4 flex items-center gap-3"
            data-testid={`checklist-schedule-booked-${item.id}`}
          >
            <CalendarCheck className="w-5 h-5 text-[#6B0F1A] flex-shrink-0" strokeWidth={1.75} />
            <div>
              <div className="font-display text-sm font-bold text-[#1A0F0A]">
                You're booked in.
              </div>
              <p className="text-xs text-[#4D3F35] mt-0.5 leading-relaxed">
                We'll see you at your onboarding call. Check your inbox for the calendar invite — a Reelty admin will approve your account after the meeting.
              </p>
            </div>
          </div>
        )}

        {/* Uploaded files list — one row per file, each individually removable.
            When the list is empty the email fallback is offered instead. */}
        {canAttach && (
          <div className="mt-3 space-y-2" data-testid={`checklist-attachments-${item.id}`}>
            <AnimatePresence initial={false}>
              {attachments.map((att) => {
                const isRemoving = removingFileId === att.file_id;
                return (
                  <motion.div
                    key={att.id}
                    layout
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: isRemoving ? 0.55 : 1, y: 0 }}
                    exit={{ opacity: 0, y: -4, height: 0, marginTop: 0 }}
                    transition={{ duration: 0.22 }}
                    className="flex items-center gap-3 p-3 rounded-sm border border-[#6B0F1A]/25 bg-[#6B0F1A]/6"
                    data-testid={`checklist-attachment-${item.id}-${att.id}`}
                  >
                    <FileText className="w-4 h-4 text-[#6B0F1A] flex-shrink-0" strokeWidth={1.75} />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium text-[#1A0F0A] truncate" title={att.filename}>
                        {att.filename}
                      </div>
                      <div className="font-mono-tag text-[9px] text-[#78584B] mt-0.5">
                        {formatBytes(att.size)} · UPLOADED {new Date(att.uploaded_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeOne(att.id)}
                      disabled={isRemoving}
                      data-testid={`checklist-remove-attachment-${item.id}-${att.id}`}
                      className="font-mono-tag text-[10px] text-[#78584B] hover:text-[#6B0F1A] disabled:opacity-60 inline-flex items-center gap-1"
                      title="Remove this file"
                    >
                      <X className="w-3 h-3" strokeWidth={2} />
                      {isRemoving ? "REMOVING…" : "REMOVE"}
                    </button>
                  </motion.div>
                );
              })}
            </AnimatePresence>
            {!hasAttachments && (
              <button
                type="button"
                onClick={emailSignedCopy}
                data-testid={`checklist-email-fallback-${item.id}`}
                className="w-full sm:w-auto min-h-[36px] px-3 py-2 rounded-sm border border-[#6B0F1A]/25 bg-transparent hover:bg-[#6B0F1A]/8 hover:border-[#6B0F1A]/50 text-[#6B0F1A] inline-flex items-center justify-center sm:justify-start gap-2 text-xs font-medium tracking-tight transition-colors"
              >
                <Mail className="w-3.5 h-3.5 flex-shrink-0" strokeWidth={2} />
                <span className="underline underline-offset-2 decoration-[#6B0F1A]/40">Click here to email a copy</span>
              </button>
            )}
          </div>
        )}
      </div>

      <div className="flex-shrink-0">
        {item.completed ? (
          <CheckCircle2 className="w-5 h-5 text-[#6B0F1A]" strokeWidth={1.75} />
        ) : (
          <Circle className="w-5 h-5 text-[#1A0F0A]/20" strokeWidth={1.5} />
        )}
      </div>
    </motion.div>
  );
}

// Memoized so unrelated items don't re-render when a sibling toggles.
export const ChecklistCard = memo(ChecklistCardBase);
